<template>
  <Page :fill="false">
    <div class="flex flex-col gap-3">
      <!-- Header -->
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden p-6">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
              <i class="fa-solid fa-clipboard-check text-xl"></i>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-primary">Data Quality Report</h1>
              <p class="text-secondary">Completeness, validity, and maintenance integrity at a glance</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="pi pi-file-export"
              label="Export CSV"
              severity="secondary"
              outlined
              class="rounded-full"
              :disabled="completenessLoading || !completenessReport"
              @click="handleExportCompletenessCsv"
            />
            <Button
              icon="pi pi-file-pdf"
              label="Export PDF"
              severity="secondary"
              outlined
              class="rounded-full"
              :disabled="!hasAnyReportData"
              @click="handleExportPdf"
            />
            <Button
              icon="pi pi-play"
              label="Run Check"
              class="rounded-full"
              :loading="integrityLoading"
              @click="handleRunAssetIntegrityCheck"
            />
          </div>
        </div>
      </div>

      <!-- Scorecard band -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div class="bg-background-card rounded-lg shadow-card border border-primary/40 p-4 text-center">
          <p class="text-xs font-semibold text-primary uppercase tracking-wide">Overall</p>
          <p class="mt-1 text-2xl font-bold text-primary">{{ formatPercent(overallPercent) }}</p>
          <p class="mt-1 text-xs text-text-light">Completeness + validity average</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Completeness</p>
          <p v-if="completenessLoading" class="mt-1 text-sm text-text-light">Loading...</p>
          <p v-else-if="completenessError" class="mt-1 text-sm text-error">{{ completenessError }}</p>
          <p v-else class="mt-1 text-2xl font-bold text-text">{{ formatPercent(completenessPercent) }}</p>
          <p class="mt-1 text-xs text-text-light">{{ completenessChecked }} assets evaluated</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Validity</p>
          <p v-if="validity" class="mt-1 text-2xl font-bold text-success">{{ formatPercent(validity.validityPercent) }}</p>
          <p v-else class="mt-1 text-sm text-text-light">Not run yet</p>
          <p class="mt-1 text-xs text-text-light">
            {{ validity ? `${validity.validityChecked} assets evaluated` : 'Run the Asset Integrity check' }}
          </p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Assets Checked</p>
          <p class="mt-1 text-2xl font-bold text-text">{{ completenessChecked }}</p>
          <p class="mt-1 text-xs text-text-light">
            {{ validity ? `${validity.validityChecked} validity` : 'validity pending' }}
          </p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Errors / Warnings</p>
          <p v-if="validity" class="mt-1 text-2xl font-bold text-text">
            {{ validity.errorCount }}<span class="text-text-light"> / </span>{{ validity.warningCount }}
          </p>
          <p v-else class="mt-1 text-2xl font-bold text-text-light">—</p>
          <p class="mt-1 text-xs text-text-light">integrity errors / warnings</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Checked At</p>
          <p class="mt-1 text-sm font-semibold text-text">{{ formatCheckedAt(checkedAtValue) }}</p>
          <p class="mt-1 text-xs text-text-light">latest report run</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="rounded-lg border border-border bg-background-card shadow-card overflow-hidden">
        <TabView class="p-1" @tab-change="onTabChange">
          <!-- Asset Integrity -->
          <TabPanel value="asset-integrity" header="Asset Integrity">
            <div class="flex flex-col gap-3 p-1">
              <div class="flex items-center justify-between gap-2">
                <div>
                  <h2 class="text-base font-semibold text-text">Asset Integrity</h2>
                  <p class="text-xs text-text-light">Scan asset data for referential integrity violations</p>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    icon="pi pi-file-export"
                    label="Export CSV"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :disabled="integrityLoading || integrityFilteredViolations.length === 0"
                    @click="handleExportIntegrityCsv"
                  />
                  <Button
                    icon="pi pi-file-pdf"
                    label="Export PDF"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :disabled="!integrityReport"
                    @click="handleExportIntegrityPdf"
                  />
                  <Button
                    icon="pi pi-play"
                    label="Run Check"
                    size="small"
                    class="rounded-full"
                    :loading="integrityLoading"
                    @click="handleRunAssetIntegrityCheck"
                  />
                </div>
              </div>

              <!-- Asset Integrity summary cards -->
              <div v-if="integrityReport" class="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Assets Checked</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ integrityReport.totalAssetsChecked }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Total Violations</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ integrityReport.totalViolationsFound }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-error/40 p-4 text-center">
                  <p class="text-xs font-semibold text-error uppercase tracking-wide">Errors</p>
                  <p class="mt-1 text-2xl font-bold text-error">{{ integrityReport.errorCount }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-warning/40 p-4 text-center">
                  <p class="text-xs font-semibold text-warning uppercase tracking-wide">Warnings</p>
                  <p class="mt-1 text-2xl font-bold text-warning">{{ integrityReport.warningCount }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Duration</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ formatDuration(integrityReport.durationMs) }}</p>
                </div>
              </div>

              <!-- Asset Integrity rule results -->
              <div
                v-if="integrityReport && integrityRuleResults.length > 0"
                class="rounded-lg border border-border bg-background-elevated overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-border">
                  <h3 class="text-sm font-semibold text-text">Rule Results</h3>
                </div>
                <DataTable
                  :value="integrityRuleResults"
                  striped-rows
                  show-gridlines
                  class="p-datatable-sm"
                >
                  <Column field="ruleName" header="Rule Name" style="min-width: 12rem" sortable>
                    <template #body="{ data }">
                      {{ data.ruleName || '—' }}
                    </template>
                  </Column>
                  <Column field="severity" header="Severity" style="min-width: 7rem" sortable>
                    <template #body="{ data }">
                      <Tag
                        v-if="data.severity"
                        :value="data.severity"
                        :severity="data.severity === 'Error' ? 'danger' : 'warn'"
                      />
                      <span v-else>—</span>
                    </template>
                  </Column>
                  <Column field="totalChecked" header="Total Checked" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.totalChecked ?? 0 }}
                    </template>
                  </Column>
                  <Column field="violationCount" header="Violation Count" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.violationCount ?? 0 }}
                    </template>
                  </Column>
                  <Column field="passed" header="Passed" style="min-width: 7rem">
                    <template #body="{ data }">
                      <Tag
                        :value="data.passed ? 'Passed' : 'Failed'"
                        :severity="data.passed ? 'success' : 'danger'"
                      />
                    </template>
                  </Column>
                </DataTable>
              </div>

              <!-- Asset Integrity filters -->
              <div v-if="integrityReport" class="rounded-lg border border-border bg-background-elevated p-4">
                <div class="flex items-center justify-between">
                  <h2 class="text-sm font-semibold text-text">Filters</h2>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-text-light">
                      {{ integrityFilteredViolations.length }} of {{ integrityViolations.length }}
                      {{ integrityViolations.length === 1 ? 'violation' : 'violations' }}
                    </span>
                    <Button
                      icon="pi pi-filter-slash"
                      label="Clear"
                      severity="secondary"
                      outlined
                      size="small"
                      class="rounded-full"
                      :disabled="!integrityHasActiveFilters"
                      @click="resetIntegrityFilters"
                    />
                  </div>
                </div>
                <div class="mt-3 grid grid-cols-12 gap-3">
                  <div class="col-span-12 lg:col-span-4">
                    <label class="text-xs font-semibold text-text-light">Search</label>
                    <InputText
                      v-model="integrityFilters.searchText"
                      class="w-full"
                      placeholder="Search asset ID, field, entity, or message"
                    />
                  </div>
                  <div class="col-span-12 md:col-span-4 lg:col-span-3">
                    <label class="text-xs font-semibold text-text-light">Severity</label>
                    <Dropdown
                      v-model="integrityFilters.severity"
                      :options="severityOptions"
                      option-label="label"
                      option-value="value"
                      class="w-full"
                    />
                  </div>
                  <div class="col-span-12 md:col-span-4 lg:col-span-3">
                    <label class="text-xs font-semibold text-text-light">Rule</label>
                    <Dropdown
                      v-model="integrityFilters.ruleName"
                      :options="integrityRuleNameOptions"
                      option-label="label"
                      option-value="value"
                      class="w-full"
                      show-clear
                      placeholder="All rules"
                    />
                  </div>
                  <div class="col-span-12 md:col-span-4 lg:col-span-2">
                    <label class="text-xs font-semibold text-text-light">Entity Type</label>
                    <Dropdown
                      v-model="integrityFilters.entityType"
                      :options="integrityEntityTypeOptions"
                      option-label="label"
                      option-value="value"
                      class="w-full"
                      show-clear
                      placeholder="All types"
                    />
                  </div>
                </div>
              </div>

              <!-- Asset Integrity violations -->
              <div
                class="relative flex min-h-80 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card"
              >
                <div
                  v-if="!integrityReport && !integrityLoading && !integrityError"
                  class="flex flex-1 items-center justify-center px-6 py-10"
                >
                  <div class="max-w-md text-center">
                    <i class="fa-solid fa-shield-halved text-4xl text-text-light mb-3"></i>
                    <h2 class="text-base font-semibold text-text">No Check Run Yet</h2>
                    <p class="mt-1 text-sm text-text-light">
                      Click "Run Check" to scan asset data for integrity violations.
                    </p>
                  </div>
                </div>

                <div
                  v-if="integrityLoading"
                  class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light"
                >
                  <i class="pi pi-spin pi-spinner mr-2"></i>
                  Running integrity check... This may take a moment.
                </div>

                <div
                  v-else-if="integrityError"
                  class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error"
                >
                  {{ integrityError }}
                </div>

                <div
                  v-else-if="integrityReport && integrityFilteredViolations.length === 0"
                  class="flex flex-1 items-center justify-center px-6 py-10"
                >
                  <div class="max-w-md text-center">
                    <i class="fa-solid fa-circle-check text-4xl text-success mb-3"></i>
                    <h2 class="text-base font-semibold text-text">
                      {{
                        integrityHasActiveFilters
                          ? 'No violations match the current filters'
                          : 'No Violations Found'
                      }}
                    </h2>
                    <p class="mt-1 text-sm text-text-light">
                      {{
                        integrityHasActiveFilters
                          ? 'Try adjusting your filters.'
                          : 'All asset data passed the integrity checks.'
                      }}
                    </p>
                  </div>
                </div>

                <div v-else-if="integrityReport && integrityFilteredViolations.length > 0">
                  <DataTable
                    :value="integrityFilteredViolations"
                    data-key="index"
                    striped-rows
                    show-gridlines
                    class="p-datatable-sm"
                    :paginator="integrityFilteredViolations.length > 50"
                    :rows="50"
                    :rows-per-page-options="[25, 50, 100]"
                    scrollable
                  >
                    <Column field="assetId" header="Asset ID" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        <span class="font-semibold">{{ data.assetId || '—' }}</span>
                      </template>
                    </Column>
                    <Column field="entityType" header="Entity Type" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        {{ data.entityType || '—' }}
                      </template>
                    </Column>
                    <Column field="fieldName" header="Field" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        <code class="text-xs bg-background-elevated px-1.5 py-0.5 rounded">{{
                          data.fieldName || '—'
                        }}</code>
                      </template>
                    </Column>
                    <Column header="Referenced Entity" style="min-width: 14rem">
                      <template #body="{ data }">
                        <div class="flex flex-col">
                          <span class="text-xs text-text-light">{{ data.referencedEntityType || '—' }}</span>
                          <span>{{ data.referencedEntityDisplayName || data.referencedEntityId || '—' }}</span>
                        </div>
                      </template>
                    </Column>
                    <Column field="ruleName" header="Rule" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        {{ data.ruleName || '—' }}
                      </template>
                    </Column>
                    <Column field="severity" header="Severity" style="min-width: 7rem" sortable>
                      <template #body="{ data }">
                        <Tag
                          :value="data.severity"
                          :severity="data.severity === 'Error' ? 'danger' : 'warn'"
                        />
                      </template>
                    </Column>
                    <Column field="message" header="Message" style="min-width: 20rem">
                      <template #body="{ data }">
                        <span class="text-sm">{{ data.message || '—' }}</span>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Maintenance Integrity -->
          <TabPanel value="maintenance-integrity" header="Maintenance Integrity">
            <div class="flex flex-col gap-3 p-1">
              <div class="flex items-center justify-between gap-2">
                <div>
                  <h2 class="text-base font-semibold text-text">Maintenance Integrity</h2>
                  <p class="text-xs text-text-light">Scan maintenance analyses for methodology violations</p>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    icon="pi pi-file-export"
                    label="Export CSV"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :disabled="maintenanceLoading || maintenanceFilteredViolations.length === 0"
                    @click="handleExportMaintenanceCsv"
                  />
                  <Button
                    icon="pi pi-file-pdf"
                    label="Export PDF"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :disabled="!maintenanceReport"
                    @click="handleExportMaintenancePdf"
                  />
                  <Button
                    icon="pi pi-play"
                    label="Run Check"
                    size="small"
                    class="rounded-full"
                    :loading="maintenanceLoading"
                    @click="handleRunMaintenanceCheck"
                  />
                </div>
              </div>

              <!-- Maintenance Integrity summary cards -->
              <div v-if="maintenanceReport" class="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Asset Types Checked</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ maintenanceReport.totalMaintenanceAssetTypesChecked }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Total Violations</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ maintenanceReport.totalViolationsFound }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-error/40 p-4 text-center">
                  <p class="text-xs font-semibold text-error uppercase tracking-wide">Errors</p>
                  <p class="mt-1 text-2xl font-bold text-error">{{ maintenanceReport.errorCount }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-warning/40 p-4 text-center">
                  <p class="text-xs font-semibold text-warning uppercase tracking-wide">Warnings</p>
                  <p class="mt-1 text-2xl font-bold text-warning">{{ maintenanceReport.warningCount }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Duration</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ formatDuration(maintenanceReport.durationMs) }}</p>
                </div>
              </div>

              <!-- Maintenance Integrity rule results -->
              <div
                v-if="maintenanceReport && maintenanceRuleResults.length > 0"
                class="rounded-lg border border-border bg-background-elevated overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-border">
                  <h3 class="text-sm font-semibold text-text">Rule Results</h3>
                </div>
                <DataTable
                  :value="maintenanceRuleResults"
                  striped-rows
                  show-gridlines
                  class="p-datatable-sm"
                >
                  <Column field="ruleName" header="Rule Name" style="min-width: 12rem" sortable>
                    <template #body="{ data }">
                      {{ data.ruleName || '—' }}
                    </template>
                  </Column>
                  <Column field="severity" header="Severity" style="min-width: 7rem" sortable>
                    <template #body="{ data }">
                      <Tag
                        v-if="data.severity"
                        :value="data.severity"
                        :severity="data.severity === 'Error' ? 'danger' : 'warn'"
                      />
                      <span v-else>—</span>
                    </template>
                  </Column>
                  <Column field="totalChecked" header="Total Checked" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.totalChecked ?? 0 }}
                    </template>
                  </Column>
                  <Column field="violationCount" header="Violation Count" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.violationCount ?? 0 }}
                    </template>
                  </Column>
                  <Column field="passed" header="Passed" style="min-width: 7rem">
                    <template #body="{ data }">
                      <Tag
                        :value="data.passed ? 'Passed' : 'Failed'"
                        :severity="data.passed ? 'success' : 'danger'"
                      />
                    </template>
                  </Column>
                </DataTable>
              </div>

              <!-- Maintenance Integrity filters -->
              <div v-if="maintenanceReport" class="rounded-lg border border-border bg-background-elevated p-4">
                <div class="flex items-center justify-between">
                  <h2 class="text-sm font-semibold text-text">Filters</h2>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-text-light">
                      {{ maintenanceFilteredViolations.length }} of {{ maintenanceViolations.length }}
                      {{ maintenanceViolations.length === 1 ? 'violation' : 'violations' }}
                    </span>
                    <Button
                      icon="pi pi-filter-slash"
                      label="Clear"
                      severity="secondary"
                      outlined
                      size="small"
                      class="rounded-full"
                      :disabled="!maintenanceHasActiveFilters"
                      @click="resetMaintenanceFilters"
                    />
                  </div>
                </div>
                <div class="mt-3 grid grid-cols-12 gap-3">
                  <div class="col-span-12 lg:col-span-6">
                    <label class="text-xs font-semibold text-text-light">Search</label>
                    <InputText
                      v-model="maintenanceFilters.searchText"
                      class="w-full"
                      placeholder="Search asset type, analysis, rule, or message"
                    />
                  </div>
                  <div class="col-span-12 md:col-span-6 lg:col-span-3">
                    <label class="text-xs font-semibold text-text-light">Severity</label>
                    <Dropdown
                      v-model="maintenanceFilters.severity"
                      :options="severityOptions"
                      option-label="label"
                      option-value="value"
                      class="w-full"
                    />
                  </div>
                  <div class="col-span-12 md:col-span-6 lg:col-span-3">
                    <label class="text-xs font-semibold text-text-light">Rule</label>
                    <Dropdown
                      v-model="maintenanceFilters.ruleName"
                      :options="maintenanceRuleNameOptions"
                      option-label="label"
                      option-value="value"
                      class="w-full"
                      show-clear
                      placeholder="All rules"
                    />
                  </div>
                </div>
              </div>

              <!-- Maintenance Integrity violations -->
              <div
                class="relative flex min-h-80 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card"
              >
                <div
                  v-if="!maintenanceReport && !maintenanceLoading && !maintenanceError"
                  class="flex flex-1 items-center justify-center px-6 py-10"
                >
                  <div class="max-w-md text-center">
                    <i class="fa-solid fa-shield-halved text-4xl text-text-light mb-3"></i>
                    <h2 class="text-base font-semibold text-text">No Check Run Yet</h2>
                    <p class="mt-1 text-sm text-text-light">
                      Click "Run Check" to scan maintenance analyses for methodology violations.
                    </p>
                  </div>
                </div>

                <div
                  v-if="maintenanceLoading"
                  class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light"
                >
                  <i class="pi pi-spin pi-spinner mr-2"></i>
                  Running maintenance integrity check... This may take a moment.
                </div>

                <div
                  v-else-if="maintenanceError"
                  class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error"
                >
                  {{ maintenanceError }}
                </div>

                <div
                  v-else-if="maintenanceReport && maintenanceFilteredViolations.length === 0"
                  class="flex flex-1 items-center justify-center px-6 py-10"
                >
                  <div class="max-w-md text-center">
                    <i class="fa-solid fa-circle-check text-4xl text-success mb-3"></i>
                    <h2 class="text-base font-semibold text-text">
                      {{
                        maintenanceHasActiveFilters
                          ? 'No violations match the current filters'
                          : 'No Violations Found'
                      }}
                    </h2>
                    <p class="mt-1 text-sm text-text-light">
                      {{
                        maintenanceHasActiveFilters
                          ? 'Try adjusting your filters.'
                          : 'All maintenance analyses passed the integrity checks.'
                      }}
                    </p>
                  </div>
                </div>

                <div v-else-if="maintenanceReport && maintenanceFilteredViolations.length > 0">
                  <DataTable
                    :value="maintenanceFilteredViolations"
                    data-key="index"
                    striped-rows
                    show-gridlines
                    class="p-datatable-sm"
                    :paginator="maintenanceFilteredViolations.length > 50"
                    :rows="50"
                    :rows-per-page-options="[25, 50, 100]"
                    scrollable
                  >
                    <Column field="maintenanceAssetTypeCode" header="Asset Type" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        <span class="font-semibold">{{ data.maintenanceAssetTypeCode || '—' }}</span>
                      </template>
                    </Column>
                    <Column field="analysisNumber" header="Analysis" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        {{ data.analysisNumber || '—' }}
                      </template>
                    </Column>
                    <Column field="ruleName" header="Rule" style="min-width: 10rem" sortable>
                      <template #body="{ data }">
                        {{ data.ruleName || '—' }}
                      </template>
                    </Column>
                    <Column field="severity" header="Severity" style="min-width: 7rem" sortable>
                      <template #body="{ data }">
                        <Tag
                          :value="data.severity"
                          :severity="data.severity === 'Error' ? 'danger' : 'warn'"
                        />
                      </template>
                    </Column>
                    <Column field="message" header="Message" style="min-width: 20rem">
                      <template #body="{ data }">
                        <span class="text-sm">{{ data.message || '—' }}</span>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Completeness -->
          <TabPanel value="completeness" header="Completeness">
            <div class="flex flex-col gap-3 p-1">
              <div class="flex items-center justify-between gap-2">
                <div>
                  <h2 class="text-base font-semibold text-text">Completeness</h2>
                  <p class="text-xs text-text-light">Completeness rules and evaluation results</p>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    icon="pi pi-refresh"
                    label="Refresh"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :loading="completenessLoading || rulesLoading"
                    @click="refreshCompletenessData"
                  />
                  <Button
                    icon="pi pi-file-export"
                    label="Export CSV"
                    severity="secondary"
                    outlined
                    size="small"
                    class="rounded-full"
                    :disabled="!completenessReport"
                    @click="handleExportCompletenessCsv"
                  />
                  <Button
                    icon="pi pi-plus"
                    label="New Rule"
                    size="small"
                    class="rounded-full"
                    @click="openCreateRule"
                  />
                </div>
              </div>

              <!-- Completeness summary cards -->
              <div v-if="completenessReport" class="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Completeness</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ formatPercent(completenessReport.completenessPercent) }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Assets Checked</p>
                  <p class="mt-1 text-2xl font-bold text-text">{{ completenessReport.completenessChecked }}</p>
                </div>
                <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
                  <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Checked At</p>
                  <p class="mt-1 text-base font-semibold text-text">{{ formatCheckedAt(completenessReport.checkedAt) }}</p>
                </div>
              </div>

              <!-- Association breakdown -->
              <div
                v-if="completenessReport && (completenessReport.associationBreakdown?.length ?? 0) > 0"
                class="rounded-lg border border-border bg-background-elevated overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-border">
                  <h3 class="text-sm font-semibold text-text">Association Breakdown</h3>
                </div>
                <DataTable :value="completenessReport.associationBreakdown" striped-rows show-gridlines class="p-datatable-sm">
                  <Column field="associationName" header="Association" style="min-width: 14rem" sortable>
                    <template #body="{ data }">
                      {{ data.associationName || data.association || '—' }}
                    </template>
                  </Column>
                  <Column field="expectedCount" header="Expected" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.expectedCount ?? 0 }}
                    </template>
                  </Column>
                  <Column field="satisfiedCount" header="Satisfied" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ data.satisfiedCount ?? 0 }}
                    </template>
                  </Column>
                  <Column header="Percent" style="min-width: 8rem">
                    <template #body="{ data }">
                      {{ formatBreakdownPercent(data.expectedCount, data.satisfiedCount) }}
                    </template>
                  </Column>
                </DataTable>
              </div>

              <!-- Rules config -->
              <div class="rounded-lg border border-border bg-background-elevated overflow-hidden">
                <div class="px-4 py-3 border-b border-border flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-text">Completeness Rules</h3>
                  <span class="text-xs text-text-light">{{ rulesTotal }} rule{{ rulesTotal === 1 ? '' : 's' }}</span>
                </div>
                <div v-if="rulesError" class="px-4 py-3 text-sm text-error">{{ rulesError }}</div>
                <DataTable
                  :value="rules"
                  :loading="rulesLoading"
                  data-key="id"
                  striped-rows
                  show-gridlines
                  class="p-datatable-sm"
                  :paginator="rulesTotal > rulesPageSize"
                  :rows="rulesPageSize"
                  :total-records="rulesTotal"
                  lazy
                  @page="onRulesPageChange"
                >
                  <Column field="association" header="Association" style="min-width: 14rem" sortable>
                    <template #body="{ data }">
                      {{ associationLabel(data.association) }}
                    </template>
                  </Column>
                  <Column field="weight" header="Weight" style="min-width: 6rem" sortable>
                    <template #body="{ data }">
                      {{ data.weight ?? '—' }}
                    </template>
                  </Column>
                  <Column header="Enabled" style="min-width: 6rem">
                    <template #body="{ data }">
                      <Checkbox
                        :model-value="data.isEnabled ?? true"
                        binary
                        :aria-label="data.isEnabled ? 'Disable rule' : 'Enable rule'"
                        @change="toggleRuleEnabled(data)"
                      />
                    </template>
                  </Column>
                  <Column field="description" header="Description" style="min-width: 16rem">
                    <template #body="{ data }">
                      <span class="text-sm">{{ data.description || '—' }}</span>
                    </template>
                  </Column>
                  <Column header="Actions" style="min-width: 8rem">
                    <template #body="{ data }">
                      <div class="flex items-center gap-1">
                        <Button
                          icon="pi pi-pencil"
                          text
                          rounded
                          size="small"
                          aria-label="Edit rule"
                          @click="openEditRule(data)"
                        />
                        <Button
                          icon="pi pi-trash"
                          text
                          rounded
                          severity="danger"
                          size="small"
                          aria-label="Delete rule"
                          @click="deleteRule(data)"
                        />
                      </div>
                    </template>
                  </Column>
                </DataTable>
              </div>

              <!-- Incomplete assets -->
              <div
                v-if="sortedIncompleteAssets.length > 0"
                class="rounded-lg border border-border bg-background-elevated overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-border">
                  <h3 class="text-sm font-semibold text-text">Incomplete Assets</h3>
                </div>
                <DataTable :value="sortedIncompleteAssets" data-key="assetId" striped-rows show-gridlines class="p-datatable-sm">
                  <Column field="assetCode" header="Asset Code" style="min-width: 12rem" sortable>
                    <template #body="{ data }">
                      <span class="font-semibold">{{ data.assetCode || '—' }}</span>
                    </template>
                  </Column>
                  <Column field="completenessPercent" header="Completeness" style="min-width: 8rem" sortable>
                    <template #body="{ data }">
                      {{ formatPercent(data.completenessPercent) }}
                    </template>
                  </Column>
                  <Column header="Failed Associations" style="min-width: 18rem">
                    <template #body="{ data }">
                      <div class="flex flex-wrap gap-1">
                        <Tag v-for="association in data.failedAssociations ?? []" :key="association" :value="association" severity="danger" />
                      </div>
                    </template>
                  </Column>
                </DataTable>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </div>

    <!-- Rule CRUD dialog -->
    <Dialog
      v-model:visible="ruleDialogVisible"
      :modal="true"
      :closable="true"
      :dismissableMask="true"
      :style="{ width: '34rem' }"
      :header="editingRuleId ? 'Edit Completeness Rule' : 'New Completeness Rule'"
      class="max-w-[95vw]!"
    >
      <div class="flex flex-col gap-4">
        <div v-if="ruleFormError" class="rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
          {{ ruleFormError }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-text-light">Entity Type</label>
          <InputText :model-value="ENTITY_TYPE_LABEL" disabled class="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-text-light">Association</label>
          <Dropdown
            v-model="ruleForm.association"
            :options="ASSOCIATION_OPTIONS"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-text-light">Weight</label>
          <InputNumber v-model="ruleForm.weight" :min="0" :max="100" class="w-full" />
        </div>
        <div class="flex items-center gap-2">
          <Checkbox v-model="ruleForm.isEnabled" binary input-id="rule-enabled" />
          <label for="rule-enabled" class="text-sm text-text">Enabled</label>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-text-light">Description</label>
          <InputText v-model="ruleForm.description" class="w-full" placeholder="Optional description" />
        </div>
      </div>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            class="rounded-full"
            @click="ruleDialogVisible = false"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            class="rounded-full"
            :loading="ruleSaving"
            @click="saveRule"
          />
        </div>
      </template>
    </Dialog>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';
import { downloadCsv, generateTimestampedFileName } from '@/shared/utils/fileDownload';
import {
  DataQualityService,
  type DataCompletenessReportDto,
} from '@/shared/services/dataQuality';
import {
  CompletenessRulesService,
  type CreateCompletenessRulePayload,
  type DataCompletenessAssociation,
  type DataCompletenessRuleDto,
} from '@/shared/services/completenessRules';
import type { IntegrityCheckReportDto } from '@/shared/services/integrityChecks';
import type { components } from '@/core/types/generated/api';
import { useIntegrityCheck } from '../composables/useIntegrityCheck';
import { useMaintenanceIntegrityCheck } from '../composables/useMaintenanceIntegrityCheck';
import {
  exportDataQualityPdf,
  type DataQualityPdfScorecard,
} from '../services/dataQualityReportPdf';

type MaintenanceIntegrityViolationDto = components['schemas']['MaintenanceIntegrityViolationDto'];
type PdfViolationDto = components['schemas']['IntegrityViolationDto'];

definePage({
  meta: {
    title: 'Data Quality Report',
  },
});

const toast = useToast();

const COMPLETENESS_TAB_INDEX = 2;
const ENTITY_TYPE = 'AssetGeneralInfo';
const ENTITY_TYPE_LABEL = 'Asset';

const ASSOCIATION_OPTIONS: { label: string; value: DataCompletenessAssociation }[] = [
  { label: 'Has BoM', value: 'HasBoM' },
  { label: 'Has Documents', value: 'HasDocuments' },
  { label: 'Has Attributes', value: 'HasAttributes' },
  { label: 'Has Parent', value: 'HasParent' },
  { label: 'Has Details', value: 'HasDetails' },
  { label: 'Has Criticality', value: 'HasCriticality' },
  { label: 'Has ExData', value: 'HasExData' },
  { label: 'Has Performance Standards', value: 'HasPerformanceStandards' },
];

const severityOptions = [
  { label: 'All Severities', value: 'all' },
  { label: 'Error', value: 'Error' },
  { label: 'Warning', value: 'Warning' },
];

// --- Asset Integrity (feeds the validity half of the scorecard) ---
const {
  report: integrityReport,
  isLoading: integrityLoading,
  error: integrityError,
  filters: integrityFilters,
  violations: integrityViolations,
  filteredViolations: integrityFilteredViolations,
  ruleResults: integrityRuleResults,
  ruleNameOptions: integrityRuleNameOptions,
  entityTypeOptions: integrityEntityTypeOptions,
  hasActiveFilters: integrityHasActiveFilters,
  runCheck: runIntegrityCheck,
  resetFilters: resetIntegrityFilters,
  exportToCsv: exportIntegrityCsv,
} = useIntegrityCheck();

// --- Maintenance Integrity (does NOT feed the scorecard) ---
const {
  report: maintenanceReport,
  isLoading: maintenanceLoading,
  error: maintenanceError,
  filters: maintenanceFilters,
  violations: maintenanceViolations,
  filteredViolations: maintenanceFilteredViolations,
  ruleResults: maintenanceRuleResults,
  ruleNameOptions: maintenanceRuleNameOptions,
  hasActiveFilters: maintenanceHasActiveFilters,
  runCheck: runMaintenanceCheck,
  resetFilters: resetMaintenanceFilters,
  exportToCsv: exportMaintenanceCsv,
} = useMaintenanceIntegrityCheck();

// --- Completeness (scorecard half + Completeness tab) ---
const completenessReport = ref<DataCompletenessReportDto | null>(null);
const completenessLoading = ref(false);
const completenessError = ref<string | null>(null);

// --- Scorecard state ---
interface ScorecardValidity {
  validityPercent: number;
  errorCount: number;
  warningCount: number;
  validityChecked: number;
  checkedAt: string | null;
}
const validity = ref<ScorecardValidity | null>(null);

const completenessPercent = computed<number | null>(() => completenessReport.value?.completenessPercent ?? null);
const completenessChecked = computed<number>(() => completenessReport.value?.completenessChecked ?? 0);
const checkedAtValue = computed<string | null>(() =>
  toIsoString(completenessReport.value?.checkedAt ?? validity.value?.checkedAt ?? null),
);
const overallPercent = computed<number | null>(() => {
  if (completenessPercent.value == null || !validity.value) return null;
  return (completenessPercent.value + validity.value.validityPercent) / 2;
});
const hasAnyReportData = computed(() =>
  Boolean(integrityReport.value || maintenanceReport.value || completenessReport.value),
);

// --- Completeness rules state ---
const rules = ref<DataCompletenessRuleDto[]>([]);
const rulesLoading = ref(false);
const rulesError = ref<string | null>(null);
const rulesPage = ref(1);
const rulesPageSize = ref(25);
const rulesTotal = ref(0);

const sortedIncompleteAssets = computed(() =>
  [...(completenessReport.value?.incompleteAssets ?? [])].sort(
    (a, b) => (a.completenessPercent ?? 0) - (b.completenessPercent ?? 0),
  ),
);

interface RuleFormState {
  association: DataCompletenessAssociation;
  weight: number | null;
  isEnabled: boolean;
  description: string;
}
const ruleDialogVisible = ref(false);
const ruleSaving = ref(false);
const editingRuleId = ref<string | null>(null);
const ruleFormError = ref<string | null>(null);
const ruleForm = ref<RuleFormState>({
  association: 'HasBoM',
  weight: 1,
  isEnabled: true,
  description: '',
});

// --- Formatting helpers ---
function toIsoString(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function formatPercent(value: number | null | undefined): string {
  if (value == null) return '—';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCheckedAt(value: string | Date | null | undefined): string {
  const iso = toIsoString(value);
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatBreakdownPercent(expected: number | null | undefined, satisfied: number | null | undefined): string {
  if (!expected) return '—';
  return formatPercent((100 * (satisfied ?? 0)) / expected);
}

function associationLabel(association: DataCompletenessAssociation | null | undefined): string {
  return ASSOCIATION_OPTIONS.find((option) => option.value === association)?.label ?? association ?? '—';
}

// --- Completeness report / rules loading ---
async function loadCompletenessReport(): Promise<void> {
  completenessLoading.value = true;
  completenessError.value = null;
  try {
    completenessReport.value = await DataQualityService.getCompletenessReport();
  } catch (loadError) {
    completenessError.value = loadError instanceof Error ? loadError.message : 'Failed to load completeness report';
  } finally {
    completenessLoading.value = false;
  }
}

async function loadRules(): Promise<void> {
  rulesLoading.value = true;
  rulesError.value = null;
  try {
    const result = await CompletenessRulesService.getPaged({
      page: rulesPage.value,
      pageSize: rulesPageSize.value,
    });
    rules.value = result.items;
    rulesTotal.value = result.page?.totalItems ?? rules.value.length;
  } catch (loadError) {
    rulesError.value = loadError instanceof Error ? loadError.message : 'Failed to load completeness rules';
  } finally {
    rulesLoading.value = false;
  }
}

async function refreshCompletenessData(): Promise<void> {
  await Promise.all([loadCompletenessReport(), loadRules()]);
}

function onTabChange(event: { index: number }): void {
  if (event.index === COMPLETENESS_TAB_INDEX) {
    void refreshCompletenessData();
  }
}

function onRulesPageChange(event: { page: number; rows: number }): void {
  rulesPage.value = event.page + 1;
  rulesPageSize.value = event.rows;
  void loadRules();
}

// --- Validity: derived from a completed Asset Integrity run ---
function computeValidity(report: IntegrityCheckReportDto): ScorecardValidity | null {
  const total = report.totalAssetsChecked ?? 0;
  if (total <= 0) return null;

  const assetsWithErrors = new Set<string>();
  for (const violation of report.violations ?? []) {
    if (violation.severity === 'Error' && violation.assetGeneralInfoId) {
      assetsWithErrors.add(violation.assetGeneralInfoId);
    }
  }

  return {
    validityPercent: (100 * (total - assetsWithErrors.size)) / total,
    errorCount: report.errorCount ?? 0,
    warningCount: report.warningCount ?? 0,
    validityChecked: total,
    checkedAt: toIsoString(report.checkedAt),
  };
}

watch(
  () => integrityReport.value,
  (report) => {
    if (report) {
      validity.value = computeValidity(report);
    }
  },
);

// --- Run handlers ---
async function handleRunAssetIntegrityCheck(): Promise<void> {
  await runIntegrityCheck();

  if (integrityError.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Check Failed',
      detail: integrityError.value,
    });
  } else if (integrityReport.value) {
    const { totalViolationsFound, errorCount, warningCount } = integrityReport.value;
    if (totalViolationsFound === 0) {
      addToast(toast, {
        severity: 'success',
        summary: 'All Clear',
        detail: 'No integrity violations found.',
      });
    } else {
      addToast(toast, {
        severity: errorCount && errorCount > 0 ? 'error' : 'warn',
        summary: 'Violations Found',
        detail: `${totalViolationsFound} violation(s): ${errorCount} error(s), ${warningCount} warning(s).`,
      });
    }
  }
}

async function handleRunMaintenanceCheck(): Promise<void> {
  await runMaintenanceCheck();

  if (maintenanceError.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Check Failed',
      detail: maintenanceError.value,
    });
  } else if (maintenanceReport.value) {
    const { totalViolationsFound, errorCount, warningCount } = maintenanceReport.value;
    if (totalViolationsFound === 0) {
      addToast(toast, {
        severity: 'success',
        summary: 'All Clear',
        detail: 'No maintenance integrity violations found.',
      });
    } else {
      addToast(toast, {
        severity: errorCount && errorCount > 0 ? 'error' : 'warn',
        summary: 'Violations Found',
        detail: `${totalViolationsFound} violation(s): ${errorCount} error(s), ${warningCount} warning(s).`,
      });
    }
  }
}

// --- CSV exports ---
function handleExportIntegrityCsv(): void {
  exportIntegrityCsv();
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: `${integrityFilteredViolations.value.length} violation(s) exported to CSV.`,
  });
}

function handleExportMaintenanceCsv(): void {
  exportMaintenanceCsv();
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: `${maintenanceFilteredViolations.value.length} violation(s) exported to CSV.`,
  });
}

function csvEscape(value: string | number | boolean | null | undefined): string {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function buildCompletenessCsv(report: DataCompletenessReportDto, rulesList: DataCompletenessRuleDto[]): string {
  const lines: string[] = [];
  lines.push('Data Quality Report - Completeness');
  lines.push(`Checked At,${csvEscape(formatCheckedAt(report.checkedAt))}`);
  lines.push(`Completeness,${csvEscape(report.completenessPercent != null ? `${report.completenessPercent}%` : '—')}`);
  lines.push(`Assets Checked,${report.completenessChecked ?? 0}`);
  lines.push('');

  lines.push('Rules');
  lines.push('Association,Weight,Enabled,Description');
  for (const rule of rulesList) {
    lines.push(
      [
        csvEscape(associationLabel(rule.association)),
        csvEscape(rule.weight),
        csvEscape(rule.isEnabled),
        csvEscape(rule.description),
      ].join(','),
    );
  }
  lines.push('');

  lines.push('Association Breakdown');
  lines.push('Association,Expected Count,Satisfied Count,Percent');
  for (const entry of report.associationBreakdown ?? []) {
    const percent = entry.expectedCount ? (100 * (entry.satisfiedCount ?? 0)) / entry.expectedCount : 0;
    lines.push(
      [
        csvEscape(entry.associationName ?? entry.association),
        csvEscape(entry.expectedCount),
        csvEscape(entry.satisfiedCount),
        csvEscape(percent.toFixed(1)),
      ].join(','),
    );
  }
  lines.push('');

  lines.push('Incomplete Assets');
  lines.push('Asset Code,Completeness Percent,Failed Associations');
  const incomplete = [...(report.incompleteAssets ?? [])].sort(
    (a, b) => (a.completenessPercent ?? 0) - (b.completenessPercent ?? 0),
  );
  for (const asset of incomplete) {
    lines.push(
      [
        csvEscape(asset.assetCode),
        csvEscape(asset.completenessPercent),
        csvEscape((asset.failedAssociations ?? []).join(' | ')),
      ].join(','),
    );
  }

  return lines.join('\n');
}

function handleExportCompletenessCsv(): void {
  if (!completenessReport.value) {
    addToast(toast, {
      severity: 'warn',
      summary: 'No Report',
      detail: 'Completeness report has not been loaded yet.',
    });
    return;
  }
  const csv = buildCompletenessCsv(completenessReport.value, rules.value);
  downloadCsv(csv, generateTimestampedFileName('data-quality-completeness', 'csv'));
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: 'Completeness report exported to CSV.',
  });
}

// --- PDF exports ---
function buildScorecard(): DataQualityPdfScorecard {
  return {
    completenessPercent: completenessPercent.value,
    validityPercent: validity.value?.validityPercent ?? null,
    overallPercent: overallPercent.value,
    completenessChecked: completenessChecked.value,
    validityChecked: validity.value?.validityChecked ?? null,
    errorCount: validity.value?.errorCount ?? null,
    warningCount: validity.value?.warningCount ?? null,
    checkedAt: checkedAtValue.value,
  };
}

function toIntegrityViolations(violations: MaintenanceIntegrityViolationDto[]): PdfViolationDto[] {
  return violations.map((violation) => ({
    assetId: violation.maintenanceAssetTypeCode ?? null,
    entityType: 'Maintenance',
    fieldName: violation.analysisNumber ?? null,
    ruleName: violation.ruleName,
    severity: violation.severity,
    message: violation.message,
  }));
}

async function handleExportPdf(): Promise<void> {
  const ruleResults =
    integrityRuleResults.value.length > 0 ? integrityRuleResults.value : maintenanceRuleResults.value;
  const violations =
    integrityViolations.value.length > 0
      ? (integrityViolations.value as unknown as PdfViolationDto[])
      : toIntegrityViolations(maintenanceViolations.value);

  await exportDataQualityPdf({
    scorecard: buildScorecard(),
    ruleResults,
    violations,
    reportTitle: 'Data Quality Report',
  });
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: 'Data quality report exported to PDF.',
  });
}

async function handleExportIntegrityPdf(): Promise<void> {
  if (!integrityReport.value) {
    addToast(toast, {
      severity: 'warn',
      summary: 'No Report',
      detail: 'Run the Asset Integrity check before exporting the PDF.',
    });
    return;
  }
  await exportDataQualityPdf({
    scorecard: buildScorecard(),
    ruleResults: integrityRuleResults.value,
    violations: integrityViolations.value as unknown as PdfViolationDto[],
    reportTitle: 'Data Quality Report - Asset Integrity',
  });
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: 'Asset Integrity report exported to PDF.',
  });
}

async function handleExportMaintenancePdf(): Promise<void> {
  if (!maintenanceReport.value) {
    addToast(toast, {
      severity: 'warn',
      summary: 'No Report',
      detail: 'Run the Maintenance Integrity check before exporting the PDF.',
    });
    return;
  }
  await exportDataQualityPdf({
    scorecard: buildScorecard(),
    ruleResults: maintenanceRuleResults.value,
    violations: toIntegrityViolations(maintenanceViolations.value),
    reportTitle: 'Data Quality Report - Maintenance Integrity',
  });
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: 'Maintenance Integrity report exported to PDF.',
  });
}

// --- Rule CRUD ---
function openCreateRule(): void {
  editingRuleId.value = null;
  ruleForm.value = {
    association: 'HasBoM',
    weight: 1,
    isEnabled: true,
    description: '',
  };
  ruleFormError.value = null;
  ruleDialogVisible.value = true;
}

function openEditRule(rule: DataCompletenessRuleDto): void {
  editingRuleId.value = rule.id ?? null;
  ruleForm.value = {
    association: rule.association,
    weight: rule.weight ?? 1,
    isEnabled: rule.isEnabled ?? true,
    description: rule.description ?? '',
  };
  ruleFormError.value = null;
  ruleDialogVisible.value = true;
}

async function saveRule(): Promise<void> {
  ruleFormError.value = null;
  const payload: CreateCompletenessRulePayload = {
    entityType: ENTITY_TYPE,
    association: ruleForm.value.association,
    weight: ruleForm.value.weight ?? 1,
    isEnabled: ruleForm.value.isEnabled,
    description: ruleForm.value.description.trim() || null,
  };

  ruleSaving.value = true;
  try {
    if (editingRuleId.value) {
      await CompletenessRulesService.update(editingRuleId.value, {
        association: payload.association,
        weight: payload.weight,
        isEnabled: payload.isEnabled,
        description: payload.description,
      });
      addToast(toast, {
        severity: 'success',
        summary: 'Rule Updated',
        detail: 'Completeness rule updated.',
      });
    } else {
      await CompletenessRulesService.create(payload);
      addToast(toast, {
        severity: 'success',
        summary: 'Rule Created',
        detail: 'Completeness rule created.',
      });
    }
    ruleDialogVisible.value = false;
    await refreshCompletenessData();
  } catch (saveError) {
    ruleFormError.value = saveError instanceof Error ? saveError.message : 'Failed to save completeness rule';
  } finally {
    ruleSaving.value = false;
  }
}

async function toggleRuleEnabled(rule: DataCompletenessRuleDto): Promise<void> {
  if (!rule.id) return;
  const nextEnabled = !(rule.isEnabled ?? true);
  try {
    await CompletenessRulesService.bulkSetEnabled([rule.id], nextEnabled);
    addToast(toast, {
      severity: 'success',
      summary: nextEnabled ? 'Rule Enabled' : 'Rule Disabled',
      detail: `${associationLabel(rule.association)} rule ${nextEnabled ? 'enabled' : 'disabled'}.`,
    });
    await refreshCompletenessData();
  } catch (toggleError) {
    addToast(toast, {
      severity: 'error',
      summary: 'Update Failed',
      detail: toggleError instanceof Error ? toggleError.message : 'Failed to update rule.',
    });
  }
}

async function deleteRule(rule: DataCompletenessRuleDto): Promise<void> {
  if (!rule.id) return;
  try {
    await CompletenessRulesService.remove(rule.id);
    addToast(toast, {
      severity: 'success',
      summary: 'Rule Deleted',
      detail: `${associationLabel(rule.association)} rule deleted.`,
    });
    await refreshCompletenessData();
  } catch (deleteError) {
    addToast(toast, {
      severity: 'error',
      summary: 'Delete Failed',
      detail: deleteError instanceof Error ? deleteError.message : 'Failed to delete rule.',
    });
  }
}

onMounted(() => {
  void loadCompletenessReport();
});
</script>

<style scoped>
@reference '../../../style.css';
:deep(.p-datatable-thead > tr > th) {
  @apply bg-background-elevated text-xs font-semibold uppercase tracking-wide text-text-light;
}
</style>
